/**
 * The CV and personal letter a careers applicant uploads: where they are
 * stored, which files are accepted, and how a file's content is checked. The
 * website's application form (`website/src/components/forms/ApplicationFlow.tsx`)
 * uses these rules to refuse a bad file before uploading it; the backend
 * (`forms/applications.ts`) applies them again to what was actually stored,
 * which is the check that counts — anyone can upload without the form. The
 * storage rules (`backend/storage.portal.rules`, `applications/`) enforce the
 * name, type and size limits at upload.
 *
 * The website imports an identical copy
 * (`website/src/components/forms/applicationFiles.shared.ts`);
 * `applicationFiles.shared.test.ts` fails when the two differ, so edit both.
 *
 * Self-contained on purpose (no imports) so the two copies stay byte-equal.
 */

export const APPLICATION_FILE_KINDS = ["cv", "letter"] as const;
export type ApplicationFileKind = (typeof APPLICATION_FILE_KINDS)[number];

/** Accepted formats. Old `.doc` and macro-enabled `.docm` are not among them:
 *  both can carry macros. */
export const APPLICATION_FILE_TYPES = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
} as const;
export type ApplicationFileExtension = keyof typeof APPLICATION_FILE_TYPES;

export const APPLICATION_FILE_MAX_BYTES = 10 * 1024 * 1024;

/** The only object names under an application's folder: `cv.pdf`, `letter.docx`, … */
export const APPLICATION_FILE_NAME_RE = /^(cv|letter)\.(pdf|docx)$/;

/** Uploads live in the portal bucket (internal: staff data, default-deny). */
export const applicationsBucket = (projectId: string) => `${projectId}-portal`;

/** An application's folder: its `forms` doc id. */
export const applicationFolder = (formId: string) => `applications/${formId}/`;

export const applicationFilePath = (
  formId: string,
  kind: ApplicationFileKind,
  extension: ApplicationFileExtension
) => `${applicationFolder(formId)}${kind}.${extension}`;

export type ApplicationFileProblem =
  | "type" // not a PDF or Word .docx by name and declared type
  | "size" // empty, or over the limit
  | "content" // the bytes are not a PDF or Word document
  | "unsafe"; // a PDF with scripts or embedded files, a Word file with macros or embedded objects

/** The extension a picked file will be stored under, from its name and the
 *  browser's declared type; null when it is neither format. */
export function applicationFileExtension(
  name: string,
  type: string
): ApplicationFileExtension | null {
  const lower = name.toLowerCase();
  if (
    lower.endsWith(".pdf") &&
    (type === "" || type === APPLICATION_FILE_TYPES.pdf)
  ) {
    return "pdf";
  }
  if (
    lower.endsWith(".docx") &&
    (type === "" || type === APPLICATION_FILE_TYPES.docx)
  ) {
    return "docx";
  }
  return null;
}

/** Latin-1 view of the bytes, for searching the plain-text markers below. */
const latin1 = (bytes: Uint8Array): string => {
  let out = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    out += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return out;
};

const startsWith = (bytes: Uint8Array, prefix: number[]) =>
  prefix.every((b, i) => bytes[i] === b);

/** A PDF name as a pattern, matching it also when any of its characters is
 *  written as a `#xx` escape: readers take `/J#61vaScript` as `/JavaScript`. */
const pdfName = (name: string, after: string) =>
  new RegExp(
    `/${[...name]
      .map((c) => {
        const [hi, lo] = c.charCodeAt(0).toString(16);
        return `(?:${c}|#${hi}[${lo.toLowerCase()}${lo.toUpperCase()}])`;
      })
      .join("")}${after}`
  );

/** What a PDF must not contain: scripts, actions that launch programs, and
 *  embedded files. Searched in the raw bytes, so a marker hidden in a
 *  compressed object stream gets through; this stops the common cases, and
 *  the files are only ever opened by staff in Slack's viewer or their own. */
const PDF_UNSAFE = [
  pdfName("JavaScript", "\\b"),
  pdfName("JS", "[\\s(<[/]"),
  pdfName("Launch", "\\b"),
  pdfName("EmbeddedFile", "\\b"),
  pdfName("EmbeddedFiles", "\\b"),
  pdfName("RichMedia", "\\b"),
  pdfName("XFA", "\\b")
];

/** What a Word file must not contain: macros, ActiveX controls and embedded
 *  objects (which can wrap executables). A .docx is a zip whose entry names
 *  are stored uncompressed, so these are found wherever Word itself puts
 *  them; a hand-made file can store such a part under another name (its
 *  compressed relationships say where), and external links (a remote
 *  template, a linked object) aren't looked for at all. */
const DOCX_UNSAFE = [
  "vbaProject.bin",
  "vbaData.xml",
  "word/activeX/",
  "word/embeddings/"
];

/**
 * Check a file's content against the format its name claims. Returns null
 * when it is a plain PDF or Word document, or the problem.
 */
export function checkApplicationFile(
  extension: ApplicationFileExtension,
  bytes: Uint8Array
): ApplicationFileProblem | null {
  if (bytes.length === 0 || bytes.length > APPLICATION_FILE_MAX_BYTES) {
    return "size";
  }
  const text = latin1(bytes);
  if (extension === "pdf") {
    // "%PDF-"; readers accept it anywhere in the first 1024 bytes.
    const header = text.slice(0, 1024).indexOf("%PDF-");
    if (header === -1) return "content";
    return PDF_UNSAFE.some((re) => re.test(text)) ? "unsafe" : null;
  }
  // A zip ("PK\x03\x04") holding a Word document's main part.
  if (!startsWith(bytes, [0x50, 0x4b, 0x03, 0x04])) return "content";
  if (
    !text.includes("[Content_Types].xml") ||
    !text.includes("word/document.xml")
  ) {
    return "content";
  }
  return DOCX_UNSAFE.some((marker) => text.includes(marker)) ? "unsafe" : null;
}
